import { DocumentData } from "firebase/firestore"

import { userAtoms } from "#atoms"
import GenericFormModal from "#blocks/Form/GenericFormModal"
import Firebase from "#classes/Firebase"
import { User } from "#classes/User"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import {
  type AddUserFormSubmitValues,
  type AddUserFormValues,
  UserConstructor,
} from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import { useFirebaseTransaction } from "#hooks/reactQuery/useFirebaseMutation"
import { canAssignRole } from "#utils/permissionsHelpers"

import AddUserForm from "./AddUserForm"
import UserProfile from "./UserProfile"

export interface AddUserFormModalProps {
  onClose: () => void
  userId: Uuid | undefined
}

const AddUserFormModal = ({ onClose, userId }: AddUserFormModalProps) => {
  const isEdit = userId != null

  const { canCreate, canUpdate, canDelete } = useResourcePermissions(
    RESOURCES.USER
  )
  const { effectiveRole } = useUserContext()

  const initialValues = useOneDoc<UserConstructor>(
    userAtoms,
    DOC_TYPES.USERS,
    userId
  )

  // Errors are handled in GenericFormModal
  const handleFinish = async (
    cleanedValues: AddUserFormSubmitValues
  ): Promise<void> => {
    if (!canCreate && !isEdit) {
      throw new Error("You do not have permission to create users")
    }
    if (!canUpdate && isEdit) {
      throw new Error("You do not have permission to update users")
    }

    const submitData: AddUserFormSubmitValues = {
      ...cleanedValues,
    }

    if (!canAssignRole(effectiveRole, submitData.role)) {
      throw new Error("You cannot assign a role higher than your own")
    }

    if (
      isEdit &&
      initialValues?.role &&
      submitData.role !== initialValues.role &&
      !canAssignRole(effectiveRole, initialValues.role)
    ) {
      throw new Error("You cannot edit the role of someone above your own role")
    }

    submitData.name ??= new User(
      submitData as unknown as UserConstructor
    ).getName()

    await Firebase.getInstance().createOrUpdateUser(submitData)
  }

  const { mutate: handleTransaction } = useFirebaseTransaction()

  const handleDelete = async () => {
    if (!canDelete) {
      throw new Error("You do not have permission to delete users")
    }

    if (!initialValues?.id) return

    // Get classes from attendance
    const classIds = Object.keys(initialValues.teacherAttendance ?? {})

    const classesPromise =
      Firebase.getInstance().getDocumentsByIds<ClassConstructor>(
        DOC_TYPES.CLASSES,
        classIds
      )

    const tryoutsPromise =
      Firebase.getInstance().variableQuery<TryoutConstructor>(
        DOC_TYPES.TRYOUTS,
        {
          queries: {
            ["step2.recommendedTeacherId"]: {
              value: initialValues.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        }
      )

    // TODO: Add evals
    const evalsPromise = Firebase.getInstance().variableQuery<DocumentData>(
      DOC_TYPES.EVALUATIONS,
      {
        queries: {
          teacherId: {
            value: initialValues.id,
            comparator: COMPARATORS.EQUALS,
          },
        },
      }
    )

    const [classes, tryouts, evals] = await Promise.all([
      classesPromise,
      tryoutsPromise,
      evalsPromise,
    ])

    const errors: string[] = []

    if (classes.length > 0) {
      const message = `Please remove this user from the following classes before deleting them: 
      
${classes.map((c) => c.name).join("\n")}`
      errors.push(message)
    }

    if (tryouts.length > 0) {
      const message = `Please remove this user from the following tryouts before deleting them: 
      
${tryouts.map((t) => t.id).join("\n")}`
      errors.push(message)
    }

    if (evals.length > 0) {
      const message = `Please remove this user from the following evals before deleting them: 
      
${evals.map((e) => e.id).join("\n")}`
      errors.push(message)
    }

    if (errors.length > 0) {
      throw new Error(errors.join("\n\n"))
    }

    await handleTransaction([
      {
        docType: DOC_TYPES.USERS,
        id: initialValues.id,
        data: null,
        action: "delete",
      },
    ])

    // Brief delay to allow Firebase listeners to process the deletion
    setTimeout(() => {
      onClose()
    }, 500)
  }

  return (
    <GenericFormModal<AddUserFormValues, AddUserFormSubmitValues>
      title={isEdit ? `${initialValues?.name}` : "Add new user"}
      onClose={onClose}
      initialValues={initialValues}
      onFinish={handleFinish}
      resource={RESOURCES.USER}
      onDelete={handleDelete}
    >
      {isEdit ? (
        <UserProfile initialValues={initialValues as AddUserFormValues} />
      ) : (
        <AddUserForm initialValues={initialValues} />
      )}
    </GenericFormModal>
  )
}

export default AddUserFormModal
